import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn, Mail, Lock, ArrowRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fixtrack-25/artifacts/fon8nfqi_Disen%CC%83o%20sin%20ti%CC%81tulo.png";
const BG_IMAGE = "https://images.pexels.com/photos/12374503/pexels-photo-12374503.jpeg";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Por favor, completa todos los campos');
      return;
    }
    
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.name}`);
      navigate(user.role === 'admin' ? '/admin' : '/client');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-[60%] relative">
        <img 
          src={BG_IMAGE} 
          alt="Industrial" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-[#020617]/80 to-transparent" />
        <div className="absolute inset-0 bg-sky-500/10" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="font-rajdhani text-5xl font-bold tracking-tight mb-4">
            GESTIÓN DE<br />
            <span className="text-sky-400">MANTENIMIENTO</span><br />
            INDUSTRIAL
          </h1>
          <p className="text-lg text-slate-300 max-w-md">
            Controla tus contratos, máquinas e incidencias en una sola plataforma profesional.
          </p>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="w-full lg:w-[40%] flex items-center justify-center p-8 bg-[#020617]">
        <div className="w-full max-w-md space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-12">
            <img src={LOGO_URL} alt="Navalec" className="w-16 h-16 object-contain" />
            <div>
              <h1 className="font-rajdhani text-3xl font-bold text-white tracking-tight">NAVALEC</h1>
              <p className="text-sm text-sky-400 font-medium tracking-widest">SOLUTIONS</p>
            </div>
          </div>
          
          {/* Form */}
          <div>
            <h2 className="font-rajdhani text-2xl font-semibold text-white mb-2">
              Iniciar Sesión
            </h2>
            <p className="text-slate-400 text-sm mb-8">
              Accede a tu panel de control
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 text-sm font-medium">
                  Correo Electrónico
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="pl-10 h-12 bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-500"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300 text-sm font-medium">
                  Contraseña
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 h-12 bg-slate-900/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-sky-500"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-sky-600 hover:bg-sky-500 text-white font-medium uppercase tracking-wider neon-glow-hover"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Iniciando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn className="w-4 h-4" />
                    Iniciar Sesión
                  </span>
                )}
              </Button>
            </form>
            
            <div className="mt-8 pt-6 border-t border-slate-800">
              <p className="text-slate-400 text-sm text-center">
                ¿Tienes un código de invitación?{' '}
                <Link 
                  to="/register" 
                  className="text-sky-400 hover:text-sky-300 font-medium inline-flex items-center gap-1"
                  data-testid="go-to-register-link"
                >
                  Regístrate aquí
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
